import { useState, useEffect } from 'react';
import { Row, Col, Badge, Image } from 'antd';
import { IClientListItem, ClientDetailsLabels } from '@/ts/common-types';
import dayjs from 'dayjs';
import { DateTimeFormats } from '@/ts/constants';
import { titleCase, convertIsoDate } from '@/ts/utils';
import { useUserTimezone } from '@/hooks/AllCustomHooks';
import { sendRequest } from '@/ts/retrieve-data';

const ClientDetails = ({ client }: { client: IClientListItem }) => {
    const currentTimeZone = useUserTimezone(client.timezones?.[0]);
    const [logoUrl, setLogoUrl] = useState<string | null>(null);

    const fetchLogoWithAuth = async (filename: string): Promise<string> => {
        const response: Response = await sendRequest({
            type: `clients/logo/${filename}`,
            method: 'GET',
        }, true, false, true);

        if (!response.ok) {
            throw new Error(`Failed to fetch logo: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        return URL.createObjectURL(blob);
    };
    useEffect(() => {
        const loadLogo = async () => {
            if (client.logo_url) {
                try {
                    const filename = client.logo_url.split('/').pop();
                    if (filename) {
                        const logoBlobUrl = await fetchLogoWithAuth(filename);
                        setLogoUrl(logoBlobUrl);
                    }
                } catch (error) {
                    console.error('Error loading logo:', error);
                    setLogoUrl(client.logo_url);
                }
            }
        };

        loadLogo();
        return () => {
            if (logoUrl && logoUrl.startsWith('blob:')) {
                URL.revokeObjectURL(logoUrl);
            }
        };
    }, [client.logo_url]);

    return (
        <div className="admin-view">
            <div className="card">
                <div className="card-header">
                    <h5 className="mb-0">Client Information</h5>
                </div>
                <div className="card-body">
                    {client.logo_url && (
                        <Row gutter={[24, 16]} className="mb-4">
                            <Col span={24}>
                                <div className="info-field">
                                    <div className="label">{ClientDetailsLabels.Logo}</div>
                                    <div className="value">
                                        <Image
                                            src={logoUrl || client.logo_url}
                                            alt={`${client.name} Logo`}
                                            width={120}
                                            height={80}
                                            style={{ 
                                                objectFit: 'contain',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '4px',
                                                padding: '8px',
                                                backgroundColor: '#fafafa'
                                            }}
                                        />
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}
                    

                    <Row gutter={[24, 16]}>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.ClientName}</div>
                                <div className="value">{client.name}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.Group}</div>
                                <div className="value">{client.client_group.name}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.Subgroup}</div>
                                <div className="value">{client.client_subgroup.name}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.Country}</div>
                                <div className="value">{client.country}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.Domain}</div>
                                <div className="value">{client.domain || 'N/A'}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.ContactName}</div>
                                <div className="value">{client.contact?.name || 'N/A'}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.ContactEmail}</div>
                                <div className="value">{client.contact?.email || 'N/A'}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.ContactPhone}</div>
                                <div className="value">{client.contact?.phone || 'N/A'}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.RegisteredBy}</div>
                                <div className="value">{client.sys_registered_by_user || 'N/A'}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.RegisteredOn}</div>
                                <div className="value">
                                    {client.registered_on ? (
                                        <div>
                                            <div>{convertIsoDate(String(client.registered_on), currentTimeZone)}</div>
                                            <div className="small text-muted">
                                                {convertIsoDate(String(client.registered_on))} {DateTimeFormats.UTC_SUFFIX}
                                            </div>
                                        </div>
                                    ) : 'N/A'}
                                </div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.Status}</div>
                                <div className="value">
                                    <Badge 
                                        status={client.status ? "success" : "error"} 
                                        text={<span style={{ color: client.status ? '#52c41a' : '#ff4d4f' }}>
                                            {client.status ? 'Active' : 'Inactive'}
                                        </span>} 
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h5 className="mb-0">Subscription</h5>
                </div>
                <div className="card-body">
                    <Row gutter={[24, 16]}>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.Plan}</div>
                                <div className="value">{client.subscription.plan_name}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.Tier}</div>
                                <div className="value">{client.subscription.tier}</div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.StartDate}</div>
                                <div className="value">
                                    {client.subscription.start_date 
                                        ? dayjs(client.subscription.start_date).format(DateTimeFormats.DATE_ONLY) 
                                        : 'N/A'}
                                </div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.EndDate}</div>
                                <div className="value">
                                    {client.subscription.end_date 
                                        ? dayjs(client.subscription.end_date).format(DateTimeFormats.DATE_ONLY) 
                                        : 'N/A'}
                                </div>
                            </div>
                        </Col>
                        <Col span={6}>
                            <div className="info-field">
                                <div className="label">{ClientDetailsLabels.SubscriptionStatus}</div>
                                <div className="value">
                                    <Badge 
                                        status={client.subscription.status === 'active' ? "success" : "error"} 
                                        text={<span style={{ color: client.subscription.status === 'active' ? '#52c41a' : '#ff4d4f' }}>
                                            {client.subscription.status === 'active' ? 'Active' : 'Inactive'}
                                        </span>} 
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h5 className="mb-0">Registrations</h5>
                </div>
                <div className="card-body">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>User</th>
                                <th>Platform</th>
                            </tr>
                        </thead>
                        <tbody>
                            {client.registrations && client.registrations.length > 0 ? (
                                client.registrations.map((reg, index) => (
                                    <tr key={index}>
                                        <td>
                                            {reg.date ? (
                                                <div>
                                                    <div>{convertIsoDate(String(reg.date), currentTimeZone)}</div>
                                                    <div className="small text-muted">
                                                        {convertIsoDate(String(reg.date))} {DateTimeFormats.UTC_SUFFIX}
                                                    </div>
                                                </div>
                                            ) : 'N/A'}
                                        </td>
                                        <td>{reg.user || 'N/A'}</td>
                                        <td>{reg.job_platform || 'N/A'}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={3} className="text-center">No registration data available</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h5 className="mb-0">Modifications</h5>
                </div>
                <div className="card-body">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>User</th>
                                <th>Platform</th>
                            </tr>
                        </thead>
                        <tbody>
                            {client.modifications && client.modifications.length > 0 ? (
                                client.modifications.map((mod, index) => (
                                    <tr key={index}>
                                        <td>
                                            {mod.date ? (
                                                <div>
                                                    <div>{convertIsoDate(String(mod.date), currentTimeZone)}</div>
                                                    <div className="small text-muted">
                                                        {convertIsoDate(String(mod.date))} {DateTimeFormats.UTC_SUFFIX}
                                                    </div>
                                                </div>
                                            ) : 'N/A'}
                                        </td>
                                        <td>{mod.user || 'N/A'}</td>
                                        <td>{mod.job_platform || 'N/A'}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={3} className="text-center">No modification data available</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h5 className="mb-0">Channels</h5>
                </div>
                <div className="card-body">
                    {client.channel_details && client.channel_details.length > 0 
                        ? client.channel_details.map((channel, index) => (
                            <span key={index} className="channel-tag">
                                {channel.logo_url && <img src={channel.logo_url} alt={titleCase(channel.name)} className="channel-logo" />}
                                {titleCase(channel.name)}
                            </span>
                        ))
                        : 'N/A'
                    }
                </div>
            </div>
        </div>
    );
};

export default ClientDetails; 